import { googleGeocoder, googlePrediction } from "Utils/maps";
import { debounce } from "lodash";
import { OptionProps } from "melodies-source/Autocomplete";
import { SvgLocation } from "melodies-source/Svgs/Location";
import { SetStateAction, useEffect, useMemo, useState } from "react";
import { useGoogleMapsScript } from "./useGoogleMapsScript";

interface PlacesAutocomplete {
  loading: boolean;
  isLoaded: boolean;
  setQuery: React.Dispatch<SetStateAction<string>>;
  query: string;
  results: OptionProps[];
  getAddressDetails: (v: OptionProps) => Promise<
    | {
        place: google.maps.GeocoderResult;
        placeName: string;
      }
    | undefined
  >;
}

export const usePlacesAutocomplete = (): PlacesAutocomplete => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<OptionProps[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const { isLoaded } = useGoogleMapsScript();

  const handleSearch = useMemo(() => {
    return debounce(async (queryString) => {
      setLoading(true);
      const results = await googlePrediction(queryString);
      if (results) {
        const places = results
          ?.filter((r) => !!r.secondaryText?.text)
          ?.map((c) => ({
            icon: <SvgLocation />,
            label: c.mainText?.text ?? c.text.text,
            caption: c.secondaryText?.text,
            value: c.placeId,
          }));
        setResults(places);
        setLoading(false);
      }
    }, 500);
  }, []);

  useEffect(() => {
    if (!isLoaded) return;
    if (!!query) {
      handleSearch(query);
    } else {
      handleSearch.cancel();
      setResults([]);
    }
    return () => {
      handleSearch.cancel();
    };
  }, [query, isLoaded]);

  const getAddressDetails = async (selected: OptionProps) => {
    const placeId = selected.value;
    const placeName = selected.label;
    const places = await googleGeocoder(placeId);
    if (places.length) {
      return { place: places[0], placeName };
    }
  };

  return { loading, isLoaded, setQuery, query, results, getAddressDetails };
};
